// frontend/src/components/PraticheAutoOnlineAccess.tsx
import React, { useEffect, useState } from 'react';
import { ExternalLink } from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import { apriPortalePaol, getPaolSedi, getPaolSso, PaolSede } from '../services/praticheAutoOnline';

const PraticheAutoOnlineAccess: React.FC = () => {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [error, setError] = useState(false);
  const [sedi, setSedi] = useState<PaolSede[]>([]);
  const [selected, setSelected] = useState<string>('');
  const [launching, setLaunching] = useState(false);

  useEffect(() => {
    getPaolSedi()
      .then((r) => {
        setEnabled(r.enabled);
        setSedi(r.sedi);
        if (r.sedi.length) setSelected(r.sedi[0].key);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  const launch = async (key: string) => {
    try {
      setLaunching(true);
      const sso = await getPaolSso(key);
      if (sso.method === 'POST' && !sso.ssoUrl) {
        throw new Error('Endpoint SSO non configurato');
      }
      // Ripristino di sicurezza: se la navigazione non parte (es. CSP che blocca il form),
      // dopo qualche secondo riabilita il pulsante e avvisa. In caso di successo la pagina
      // viene sostituita e questo timeout non scatta.
      window.setTimeout(() => {
        setLaunching(false);
        toast.error('Impossibile aprire Pratiche Auto Online. Riprova.');
      }, 6000);
      // GET: navigazione all'URL firmato; POST: form auto-inviante (token nel body, non nell'URL)
      apriPortalePaol(sso);
    } catch (e) {
      toast.error('Impossibile aprire Pratiche Auto Online. Riprova.');
      setLaunching(false);
    }
  };

  if (loading) return null;

  if (error) return null;

  if (!enabled) {
    return (
      <p className="text-sm text-center">
        <strong className="font-bold bg-yellow-200 dark:bg-yellow-500/30 text-gray-900 dark:text-yellow-200 px-1.5 py-0.5 rounded box-decoration-clone">
          Non appena il nuovo servizio sarà disponibile, gli associati saranno avvisati via email e qui nel centro notifiche
        </strong>
      </p>
    );
  }

  if (!sedi.length) return null;

  return (
    <div className="space-y-3">
      {sedi.length > 1 && (
        <div>
          <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
            Scegli la sede da cui operare
          </label>
          <select
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            className="w-full text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-gray-900 dark:text-white"
          >
            {sedi.map((s) => (
              <option key={s.key} value={s.key}>{s.label}</option>
            ))}
          </select>
        </div>
      )}
      <button
        onClick={() => launch(selected || sedi[0].key)}
        disabled={launching}
        className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-br from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white text-sm font-bold px-4 py-2.5 rounded-lg shadow transition-colors disabled:opacity-60"
      >
        <ExternalLink className="w-4 h-4" />
        {launching ? 'Apertura in corso…' : 'Accedi a Pratiche Auto Online'}
      </button>
    </div>
  );
};

export default PraticheAutoOnlineAccess;
